import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  CircularProgress,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useTheme, Theme } from "@mui/material/styles";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { useNavigate } from "react-router-dom";

import BasePaper from "components/Base/BasePaper";
import { RectangularTextButton } from "components/Base/Buttons";
import NoRecordsFound from "components/Records/NoRecordsFound";

export type ColumnType =
  | "text"
  | "number"
  | "currency"
  | "percent"
  | "boolean"
  | "date"
  | "datetime";

export interface Column<T> {
  /**
   * Supports:
   * - keyof T (simple field)
   * - string dot-path: "people.first_name"
   */
  field: keyof T | string;
  label: string;

  /** How to format/display the field */
  type?: ColumnType;

  /**
   * Optional: override how the raw value is retrieved.
   * Useful for computed columns or complex shapes.
   */
  valueGetter?: (row: T) => any;

  /**
   * Optional: fully custom cell renderer (wins over type formatting).
   */
  render?: (value: any, row: T) => React.ReactNode;

  /** Optional formatting */
  dateFormatOptions?: Intl.DateTimeFormatOptions; // date/datetime
  numberFormatOptions?: Intl.NumberFormatOptions; // number/currency/percent

  /** Optional display tweaks */
  booleanLabels?: { trueLabel?: string; falseLabel?: string };
  placeholder?: string; // default "—"
  align?: "left" | "center" | "right";
}

interface StandardTableProps<T extends { id: number | string }> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  error?: string | null;
  selectable?: boolean;

  /**
   * Existing behavior (kept): called on row click.
   * If getRowLink is provided, navigation will happen first (unless disabled).
   */
  linkHandler?: (row: T) => void;

  /**
   * NEW: Return a URL for each row (e.g. row => `/community/people/${row.id}`).
   * If it returns undefined/null/"" the row won't navigate.
   */
  getRowLink?: (row: T) => string | undefined | null;

  /** If you want to prevent auto navigation and handle yourself */
  disableAutoNavigate?: boolean;

  onSelectionChange?: (selectedRows: T[]) => void;

  enableExport?: boolean;
  exportFileName?: string;
  title?: string;
  includePaper?: boolean;
}

function getValueByPath(obj: any, path: string): any {
  if (!obj || !path) return undefined;
  // fast path: no dots
  if (!path.includes(".")) return obj[path];

  return path.split(".").reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

function formatCellValue<T>(
  col: Column<T>,
  rawValue: any,
  row: T
): { display: React.ReactNode; exportText: string } {
  const placeholder = col.placeholder ?? "—";

  // Custom render wins
  if (col.render) {
    const rendered = col.render(rawValue, row);
    // Export: try to stringify raw value when custom render is used
    const exportText = rawValue == null ? "" : String(rawValue);
    return { display: rendered, exportText };
  }

  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return { display: placeholder, exportText: "" };
  }

  const type = col.type ?? "text";

  // Helpers
  const exportText = (() => {
    if (rawValue == null) return "";
    if (rawValue instanceof Date) return rawValue.toISOString();
    return String(rawValue);
  })();

  switch (type) {
    case "boolean": {
      const v = Boolean(rawValue);
      const trueLabel = col.booleanLabels?.trueLabel ?? "Yes";
      const falseLabel = col.booleanLabels?.falseLabel ?? "No";
      return { display: v ? trueLabel : falseLabel, exportText: v ? "true" : "false" };
    }

    case "number": {
      const n = typeof rawValue === "number" ? rawValue : Number(rawValue);
      if (Number.isNaN(n)) return { display: String(rawValue), exportText };
      const fmt = new Intl.NumberFormat(undefined, col.numberFormatOptions);
      return { display: fmt.format(n), exportText: String(n) };
    }

    case "currency": {
      const n = typeof rawValue === "number" ? rawValue : Number(rawValue);
      if (Number.isNaN(n)) return { display: String(rawValue), exportText };
      const fmt = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "GBP",
        ...col.numberFormatOptions,
      });
      return { display: fmt.format(n), exportText: String(n) };
    }

    case "percent": {
      const n = typeof rawValue === "number" ? rawValue : Number(rawValue);
      if (Number.isNaN(n)) return { display: String(rawValue), exportText };
      const fmt = new Intl.NumberFormat(undefined, {
        style: "percent",
        ...col.numberFormatOptions,
      });
      return { display: fmt.format(n), exportText: String(n) };
    }

    case "date":
    case "datetime": {
      // Accept Date, ISO string, timestamp number
      const d =
        rawValue instanceof Date
          ? rawValue
          : typeof rawValue === "number"
          ? new Date(rawValue)
          : new Date(String(rawValue));

      if (Number.isNaN(d.getTime())) return { display: String(rawValue), exportText };

      const options: Intl.DateTimeFormatOptions =
        col.dateFormatOptions ??
        (type === "date"
          ? { year: "numeric", month: "2-digit", day: "2-digit" }
          : {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            });

      const fmt = new Intl.DateTimeFormat(undefined, options);
      return { display: fmt.format(d), exportText: d.toISOString() };
    }

    case "text":
    default:
      return { display: String(rawValue), exportText };
  }
}

export default function GenericTable<T extends { id: number | string }>({
  data,
  columns,
  loading = false,
  error = null,
  selectable = false,
  linkHandler,
  getRowLink,
  disableAutoNavigate = false,
  onSelectionChange,
  enableExport = true,
  exportFileName = "table_export.csv",
  title,
  includePaper = true,
}: StandardTableProps<T>) {
  const theme = useTheme<Theme & { custom?: any }>();
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState<(number | string)[]>([]);
  const [columnOrder, setColumnOrder] = useState<(string)[]>(() =>
    columns.map((c) => String(c.field))
  );
  const lastEmittedSelectionRef = React.useRef<(number | string)[]>([]);

  useEffect(() => {
    if (!onSelectionChange) return;

    const normalized = [...selectedRows].sort();
    const last = lastEmittedSelectionRef.current;

    const isSame =
      normalized.length === last.length && normalized.every((id, i) => id === last[i]);

    if (isSame) return;

    lastEmittedSelectionRef.current = normalized;

    const selectedItems = data.filter((d) => normalized.includes(d.id));
    onSelectionChange(selectedItems);
  }, [selectedRows, data, onSelectionChange]);

  useEffect(() => {
    // If columns prop changes, keep order stable but include any new columns.
    const next = columns.map((c) => String(c.field));
    setColumnOrder((prev) => {
      const kept = prev.filter((f) => next.includes(f));
      const added = next.filter((f) => !kept.includes(f));
      return [...kept, ...added];
    });
  }, [columns]);

  const handleSelectAll = () => {
    if (selectedRows.length === data.length) setSelectedRows([]);
    else setSelectedRows(data.map((d) => d.id));
  };

  const handleSelectRow = (id: number | string) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const newOrder = Array.from(columnOrder);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);
    setColumnOrder(newOrder);
  };

  const orderedColumns = columnOrder
    .map((fieldStr) => columns.find((c) => String(c.field) === fieldStr))
    .filter(Boolean) as Column<T>[];

  const getRawValue = (row: T, col: Column<T>) => {
    if (col.valueGetter) return col.valueGetter(row);
    return getValueByPath(row as any, String(col.field));
  };

  const handleRowClick = (row: T) => {
    const url = getRowLink?.(row);

    if (!disableAutoNavigate && url) {
      navigate(url);
    }

    linkHandler?.(row);
  };

  /** Export to CSV (uses formatted exportText per column type) */
  const handleExportCSV = () => {
    if (!data || data.length === 0) return;

    const headers = orderedColumns.map((col) => col.label);

    const rows = data.map((row) =>
      orderedColumns.map((col) => {
        const raw = getRawValue(row, col);
        const { exportText } = formatCellValue(col, raw, row);

        // escape commas, quotes, newlines
        const safe = (exportText ?? "").replace(/"/g, '""');
        return `"${safe}"`;
      })
    );

    const csvContent = headers.join(",") + "\n" + rows.map((r) => r.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", exportFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const content = (
    <>
      <Grid2 container alignItems="center">
        <Grid2 size={2}></Grid2>

        <Grid2 size={8}>
          {title && (
            <Typography variant="h6" textAlign={"center"} sx={{ mb: 2 }}>
              {title}
            </Typography>
          )}
        </Grid2>

        <Grid2 size={2}>
          {enableExport && data.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <RectangularTextButton
                colour="clear"
                buttonSize="small"
                onClick={handleExportCSV}
              >
                Export CSV
              </RectangularTextButton>
            </Box>
          )}
        </Grid2>
      </Grid2>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ p: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && data.length === 0 && (
        <NoRecordsFound itemName="records" minHeight={50} maxHeight={100} />
      )}

      {!loading && !error && data.length > 0 && (
        <TableContainer
          sx={{
            width: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            display: "block",
          }}
        >
          <Box sx={{ minWidth: "max-content" }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Table stickyHeader>
                <TableHead>
                  <Droppable droppableId="columns" direction="horizontal">
                    {(provided) => (
                      <TableRow ref={provided.innerRef} {...provided.droppableProps}>
                        {selectable && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
                              checked={data.length > 0 && selectedRows.length === data.length}
                              onChange={handleSelectAll}
                              sx={{
                                color: theme.custom?.base?.primary?.dark,
                                "&.Mui-checked": { color: theme.custom?.base?.primary?.dark },
                              }}
                            />
                          </TableCell>
                        )}

                        {orderedColumns.map((col, index) => (
                          <Draggable key={String(col.field)} draggableId={String(col.field)} index={index}>
                            {(dragProvided) => (
                              <TableCell
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                align={col.align}
                                sx={{
                                  fontWeight: "bold",
                                  backgroundColor: theme.custom?.base?.background?.default?.dark,
                                  color: theme.custom?.base?.text?.primary?.dark,
                                  cursor: "grab",
                                }}
                              >
                                {col.label}
                              </TableCell>
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </TableRow>
                    )}
                  </Droppable>
                </TableHead>

                <TableBody>
                  {data.map((row) => {
                    const isSelected = selectedRows.includes(row.id);
                    const clickable = Boolean(getRowLink || linkHandler);

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => clickable && handleRowClick(row)}
                        sx={{
                          cursor: clickable ? "pointer" : "default",
                          backgroundColor: isSelected ? (theme.custom?.base?.primary?.dark ?? "#000") + "22" : "inherit",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? (theme.custom?.base?.primary?.dark ?? "#000") + "33"
                              : theme.custom?.utility?.hover?.dark,
                          },
                        }}
                      >
                        {selectable && (
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectRow(row.id)}
                              sx={{
                                color: theme.custom?.base?.primary?.dark,
                                "&.Mui-checked": { color: theme.custom?.base?.primary?.dark },
                              }}
                            />
                          </TableCell>
                        )}

                        {orderedColumns.map((col) => {
                          const raw = getRawValue(row, col);
                          const { display } = formatCellValue(col, raw, row);

                          return (
                            <TableCell key={String(col.field)} align={col.align}>
                              {display}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DragDropContext>
          </Box>
        </TableContainer>
      )}
    </>
  );

  return includePaper ? <BasePaper>{content}</BasePaper> : content;
}
